import React from 'react';
import { createRoot } from 'react-dom/client';
import Notification from '../components/Notification';

class NotificationService {
  constructor() {
    this.container = null;
    this.notifications = [];
    this.notificationId = 0;
  }

  // Initialize the notification container
  init() {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'notification-container';
      document.body.appendChild(this.container);
    }
  }

  // Show a notification
  show(options) {
    this.init();
    
    const id = ++this.notificationId;
    const notification = {
      id,
      ...options
    };

    this.notifications.push(notification);
    this.render();

    return id;
  }

  // Show success notification
  success(message, options = {}) {
    return this.show({
      type: 'success',
      message,
      ...options
    });
  }

  // Show error notification
  error(message, options = {}) {
    return this.show({
      type: 'error',
      message,
      duration: 5000, // Errors stay longer
      ...options
    });
  }

  // Show warning notification
  warning(message, options = {}) {
    return this.show({
      type: 'warning',
      message,
      ...options
    });
  }

  // Show info notification
  info(message, options = {}) {
    return this.show({
      type: 'info',
      message,
      ...options
    });
  }

  // Remove a specific notification
  remove(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.render();
  }

  // Clear all notifications
  clear() {
    this.notifications = [];
    this.render();
  }

  // Render all notifications
  render() {
    if (!this.container) return;

    // Create or get the root
    if (!this.root) {
      this.root = createRoot(this.container);
    }

    const notificationElements = this.notifications.map(notification => (
      <Notification
        key={notification.id}
        type={notification.type}
        message={notification.message}
        duration={notification.duration}
        persistent={notification.persistent}
        position={notification.position}
        onClose={() => this.remove(notification.id)}
      />
    ));

    this.root.render(notificationElements);
  }
}

// Create a singleton instance
const notificationService = new NotificationService();

// Create a hook for React components
export const useNotification = () => {
  return {
    success: (message, options) => notificationService.success(message, options),
    error: (message, options) => notificationService.error(message, options),
    warning: (message, options) => notificationService.warning(message, options),
    info: (message, options) => notificationService.info(message, options),
    show: (options) => notificationService.show(options),
    clear: () => notificationService.clear()
  };
};

export default notificationService;